import { renderTemplate } from "../../templateRenderer";
import { CLASSIC_TEMPLATE, CLASSIC_CSS } from "./loader";
import type { InvoiceTemplateContext } from "../../types";
import { IMAGE_URL } from "../../../../Constant";

const CLASSIC_PAGE_SIZES = {
  A4: { width: "210mm", pad: "18mm", font: "13px" },
  A3: { width: "297mm", pad: "24mm", font: "15px" },
  "80mm": { width: "78mm", pad: "4mm", font: "15px" },
} as const;

const BASE_TEMPLATE = `<!DOCTYPE html>
<html lang="en">
<head>
  <meta charset="UTF-8"/>
  <meta name="viewport" content="width=device-width,initial-scale=1"/>
  <title>{{ title }}</title>
  <style>{{ styles }}</style>
</head>
<body>
  {{ content | safe }}
</body>
</html>`;

function buildCssVariables(ctx: InvoiceTemplateContext): string {
  const { pageSize, isForPrint } = ctx;
  const is80 = pageSize === "80mm";
  const sz = CLASSIC_PAGE_SIZES[pageSize];

  return `
    :root {
      --inv-font:       ${sz.font};
      --inv-width:      ${sz.width};
      --inv-pad:        ${isForPrint ? (is80 ? "0" : "10mm") : sz.pad};
      --inv-bg:         #fff;
      --inv-body-pad:   ${isForPrint ? "0" : is80 ? "8px 4px" : "24px 16px"};
      --inv-h2-size:    ${is80 ? "1.1em" : "1.25em"};
      --inv-grand-size: ${is80 ? "1em" : "1.05em"};
      --inv-ftr-mt:     ${is80 ? "10px" : "18px"};
      --inv-ftr-size:   ${is80 ? "0.78em" : "0.82em"};
      --inv-page-size:  ${is80 ? "80mm auto" : pageSize};
    }
  `;
}

/** Detect whether an item is a service (no stock_id = temp/service item) */
function isServiceItem(item: any): boolean {
  return (
    item.is_service === true ||
    item.is_service === 1 ||
    item.product_type === "service" ||
    (item.stock_id === null && item.item_name)
  );
}

export function classicTemplate(ctx: InvoiceTemplateContext): string {
  const { order, config, fmt } = ctx;

  const currency = config.currency || "LKR";
  const allItems: any[] = order.items || [];
  const payments = order.payments || [];
  const dateObj = new Date(order.order_date || order.created_at);

  // ── Split items into products and services ──────────────────────────────
  const productItems = allItems.filter((i) => !isServiceItem(i));
  const serviceItems = allItems.filter((i) => isServiceItem(i));

  const productSubtotal = productItems.reduce(
    (sum: number, i: any) => sum + parseFloat(i.total_price || 0),
    0,
  );
  const serviceTotal = serviceItems.reduce(
    (sum: number, i: any) => sum + parseFloat(i.total_price || 0),
    0,
  );

  // ── Returns / exchanges ──────────────────────────────────────────────────
  const returns = (order.returns || []).map((r: any) => ({
    return_no: r.return_no || "",
    refund_type_name: r.refund_type_name || "", // cash/goods/credit
    total_returned: parseFloat(r.total_refunded || 0),
    created_at: r.created_at || r.return_date || "",
    items: (r.items || [])
      .filter((i: any) => parseFloat(i.quantity || 0) > 0)
      .map((i: any) => ({
        product_name: i.product_name || "Product",
        quantity: parseFloat(i.quantity || 0),
        price: parseFloat(i.refund_price || i.total_refund || 0),
        total_refund: parseFloat(i.total_refund || 0),
        return_type_name: i.return_type_name || "",
        is_tempory_item: !!i.is_tempory_item,
      })),
  }));
  const hasReturns = returns.some((r: any) => r.items.length > 0);
  const returnedTotal = returns.reduce(
    (sum: number, r: any) => sum + r.total_returned,
    0,
  );

  // ── Date / time ─────────────────────────────────────────────────────────
  const dateStr = `${dateObj.toLocaleDateString("en-GB", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  })}`;
  const timeStr = `${dateObj.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}`;

  // ── Cashier / vehicle ────────────────────────────────────────────────────
  const cashier =
    order.employee_name || order.cashier_name || order.username || "";

  // vehicle_no: some stores store it in customer_name for motor shops
  const vehicleNo = order.vehicle_no || order.customer_vehicle_no || "";

  // ── Logo ─────────────────────────────────────────────────────────────────
  const logoUrl = `${IMAGE_URL}/${config.logo_url}`;

  const contentContext = {
    storeName: config.store_name || "POS System",
    address: config.address || "",
    phone: config.phone || "",
    email: config.email || "",
    whatsapp: config.whatsapp || "",
    website: config.website || "",
    logoUrl,
    footer: config.receipt_footer || "Thank you for your business!",
    orderStatus: order.order_status_name || order.payment_status_name || "",
    orderNo: order.order_no || "",
    dateStr,
    timeStr,
    cashier,
    customerName: order.customer_name || "Walk-in Customer",
    customerPhone: order.customer_phone || "",
    customerAddress: order.customer_address || "",
    customerEmail: order.customer_email || "",
    vehicleNo,
    currency,
    orderNote: order.note || "",
    productItems,
    serviceItems,
    payments,
    returns,
    hasReturns,
    returnedTotal,
    productSubtotal,
    serviceTotal,
    showProductSubtotal: productItems.length > 0 && serviceItems.length > 0,
    grandTotal: order.grand_total,
    paidAmount: order.paid_amount,
    dueAmount: order.due_amount,
    discountAmount: order.discount_amount,
    exchangeAmount: order.exchange_amount,
    exchangeSourceNo: order.previous_exchange_no || "",
    otherCharges: order.other_charges,
    showDisc: parseFloat(order.discount_amount) > 0,
    showExch: parseFloat(order.exchange_amount) > 0,
    showChg: parseFloat(order.other_charges) > 0,
    chequePaid: payments
      .filter((p: any) => (p.payment_method_code || "").toLowerCase() === "cheque" || (p.payment_method_name || "").toLowerCase().includes("cheque"))
      .reduce((sum: number, p: any) => sum + parseFloat(p.amount || 0), 0),
    creditAmount: Math.max(0, parseFloat(order.due_amount || 0) - payments
      .filter((p: any) => (p.payment_method_code || "").toLowerCase() === "cheque" || (p.payment_method_name || "").toLowerCase().includes("cheque"))
      .reduce((sum: number, p: any) => sum + parseFloat(p.amount || 0), 0)),
    isA4: ctx.pageSize === "A4" || ctx.pageSize === "A3",
    pageSize: ctx.pageSize,
    totalItemsCount: productItems.length + serviceItems.length,
    paymentMethodName: (() => {
      const uniqueMethods = Array.from(new Set(
        payments.map((p: any) => p.payment_method_name || p.payment_method_code)
                .filter((name: any) => !!name)
      ));
      return uniqueMethods.length > 1 
        ? "Split (" + uniqueMethods.join(", ") + ")" 
        : (order.payment_method_name || order.payment_method_code || "N/A");
    })(),
    fmt,
  };

  const content = renderTemplate(CLASSIC_TEMPLATE, contentContext);
  const styles = buildCssVariables(ctx) + "\n" + CLASSIC_CSS;

  return renderTemplate(BASE_TEMPLATE, {
    title: `Invoice - ${order.order_no}`,
    styles,
    content,
  });
}